/**
 * Iter-235b / Iter-267 — Voice Recap recorder + AI review.
 *
 * Talent taps the big mic, records 30-90s describing how the shift
 * went, releases → upload streams to /api/post-event-recap/voice.
 * Backend (services/voice_recap_service.py) runs Whisper → GPT to
 * extract `rating`, `highlights`, `concerns`, and a short `summary`.
 *
 * Iter-267 fix (P0): the previous version had a STALE-CLOSURE bug. The
 * MediaRecorder.onstop callback was assigned at start time and captured a
 * snapshot of `uploadBlob` where React state `seconds = 0`. Every recording
 * therefore reported 0s no matter how long the talent held the button.
 *
 * Fix: track duration in a ref (`durationRef`) instead of React state, and
 * read from the ref inside the upload pipeline. Also added:
 *   • Visible "RECORDING" indicator + pulsing animation
 *   • Live microphone input-level meter (proves the mic is actually picking
 *     up audio)
 *   • Microphone-permission troubleshooting copy when getUserMedia rejects
 *   • Upload progress indicator
 *   • "I'd rather type" fallback preserved
 *
 * Mounted inside PostEventRecapModal as the default recap path; the
 * existing typed RatingFlow stays available as the "I'd rather type"
 * fallback.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Loader2, Sparkles, CheckCircle2, AlertCircle, Star, X, Volume2, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import api from '../utils/api';
import { toast } from 'sonner';

const MIN_SECONDS = 5;       // anything shorter is almost certainly a mistap
const MAX_SECONDS = 120;     // hard cap so we don't blow the upload limit

function pickMime() {
  // Safari only supports `audio/mp4`; Chrome/Firefox prefer webm/opus.
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg',
  ];
  return candidates.find((m) => MediaRecorder.isTypeSupported?.(m)) || '';
}

function friendlyMicError(err) {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return (
      'Microphone permission was blocked. Open your browser site settings, ' +
      'allow microphone access, then come back and tap the mic again.'
    );
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No microphone was detected on this device.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'Your microphone is in use by another app. Close it and try again.';
  }
  if (name === 'SecurityError') {
    return 'Microphone access requires HTTPS. Reload the page over https://.';
  }
  return err?.message || 'Could not start the microphone. Try refreshing the page.';
}

export default function VoiceRecapRecorder({ booking, onConfirmed, onCancel }) {
  const [phase, setPhase] = useState('idle');   // idle | recording | uploading | review | confirming | done
  const [seconds, setSeconds] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [micLevel, setMicLevel] = useState(0);    // 0..100, smoothed input volume
  const [showPermHelp, setShowPermHelp] = useState(false);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const meterRafRef = useRef(null);
  const durationRef = useRef(0);    // Iter-267 — authoritative duration (avoids stale closure)
  const startedAtRef = useRef(0);   // Iter-269 — wall-clock start (handles sub-second stops)
  const mimeRef = useRef('');

  useEffect(() => () => stopStream(), []);

  const stopMeter = () => {
    if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
    meterRafRef.current = null;
    try { audioCtxRef.current?.close(); } catch (_) { /* noop */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
  };

  const stopStream = () => {
    try { mediaRef.current?.state === 'recording' && mediaRef.current.stop(); } catch (_) { /* noop */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    stopMeter();
  };

  const startMeter = (stream) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) sum += buf[i];
        const avg = sum / buf.length;     // 0..255
        const pct = Math.min(100, Math.round((avg / 90) * 100));
        setMicLevel(pct);
        meterRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (_) { /* meter is non-critical */ }
  };

  const startRecording = async () => {
    setError(null);
    setShowPermHelp(false);
    setUploadProgress(0);
    durationRef.current = 0;
    startedAtRef.current = 0;
    try {
      if (typeof MediaRecorder === 'undefined') {
        throw new Error('Voice recording is not supported in this browser. Try Safari on iOS or Chrome on Android.');
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Voice recording requires HTTPS. Reload over https://.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      mimeRef.current = mime;
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      // Iter-267/269 — onstop reads from refs only. Duration is computed
      // from a wall-clock anchor so sub-second recordings still report
      // an accurate elapsed time and the user sees the real reason for
      // the rejection (e.g. "only 2s" instead of "only 0s").
      rec.onstop = () => {
        const ms = startedAtRef.current ? (Date.now() - startedAtRef.current) : 0;
        const final = Math.max(durationRef.current, Math.floor(ms / 1000));
        uploadBlob(rec.mimeType || mimeRef.current || 'audio/webm', final);
      };
      // Iter-267 — start with a 1s timeslice so the recorder flushes chunks
      // periodically. Some Android Chromecast/older Samsung browsers only
      // emit data on stop with no timeslice → empty blob on short recordings.
      rec.start(1000);
      mediaRef.current = rec;
      startedAtRef.current = Date.now();
      setPhase('recording');
      setSeconds(0);
      startMeter(stream);
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
        durationRef.current = elapsed;
        setSeconds(elapsed);
        if (elapsed >= MAX_SECONDS) stopRecording();
      }, 250);
    } catch (e) {
      const friendly = friendlyMicError(e);
      setError(friendly);
      setShowPermHelp(e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError');
      setPhase('idle');
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state === 'recording') {
      mediaRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    stopMeter();
  };

  const uploadBlob = async (mime, duration) => {
    // Iter-267 — `duration` comes from durationRef (passed in), not stale state.
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    if (duration < MIN_SECONDS) {
      setError(`That was only ${duration}s. Hold the recording for at least ${MIN_SECONDS}s.`);
      setPhase('idle');
      setSeconds(0);
      durationRef.current = 0;
      return;
    }

    const blob = new Blob(chunksRef.current, { type: mime });
    chunksRef.current = [];

    if (blob.size === 0) {
      setError(
        'No audio data was captured. Your microphone may have been muted by another app. ' +
        'Please try again — and if it keeps failing, switch to the "type my recap" fallback.'
      );
      setPhase('idle');
      setSeconds(0);
      durationRef.current = 0;
      return;
    }

    setPhase('uploading');
    try {
      const form = new FormData();
      form.append('booking_id', booking.id || booking.booking_id);
      form.append('duration_seconds', String(duration));
      const fileExt = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
      form.append('audio', blob, `recap.${fileExt}`);
      const res = await api.post('/post-event-recap/voice', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const ext = res.data?.extracted || {};
      setDraft({
        rating: ext.rating ?? null,
        highlights: Array.isArray(ext.highlights) ? ext.highlights : [],
        concerns: Array.isArray(ext.concerns) ? ext.concerns : [],
        summary: ext.summary || res.data?.transcript || '',
        transcript: res.data?.transcript || '',
        leads_count:          ext.leads_count ?? null,
        qualified_leads:      ext.qualified_leads ?? null,
        samples_distributed:  ext.samples_distributed ?? null,
        demos_completed:      ext.demos_completed ?? null,
        consumer_engagements: ext.consumer_engagements ?? null,
        key_takeaways:        Array.isArray(ext.key_takeaways) ? ext.key_takeaways : [],
      });
      setPhase('review');
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Upload failed.');
      setPhase('idle');
    }
  };

  const confirm = async () => {
    setPhase('confirming');
    try {
      await api.post('/post-event-recap/voice/confirm', {
        booking_id: booking.id || booking.booking_id,
        rating: draft.rating,
        highlights: draft.highlights.filter((s) => (s || '').trim()),
        concerns: draft.concerns.filter((s) => (s || '').trim()),
        summary: draft.summary || null,
        metrics: {
          leads_count:          draft.leads_count,
          qualified_leads:      draft.qualified_leads,
          samples_distributed:  draft.samples_distributed,
          demos_completed:      draft.demos_completed,
          consumer_engagements: draft.consumer_engagements,
        },
      });
      setPhase('done');
      toast.success('Recap submitted — thanks!');
      onConfirmed?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Submit failed.');
      setPhase('review');
    }
  };

  // ---------- RECORD / IDLE ----------
  if (phase === 'idle' || phase === 'recording' || phase === 'uploading') {
    const isRec = phase === 'recording';
    const isUp = phase === 'uploading';
    const isShort = isRec && seconds < MIN_SECONDS;
    return (
      <div className="flex flex-col items-center gap-4 py-6" data-testid="voice-recap-recorder">
        <div className="text-center max-w-xs">
          <p className="text-sm text-slate-700 font-medium">
            {isRec ? 'RECORDING — share how the shift went' : isUp ? 'Transcribing your recap…' : 'Tap the mic and tell us how the shift went'}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {isRec
              ? `Tap again to stop · auto-stops at ${MAX_SECONDS}s`
              : isUp
                ? 'AI is summarizing your audio.'
                : 'Easier than typing — 30-60 seconds is perfect.'}
          </p>
        </div>

        <button
          type="button"
          onClick={isRec ? stopRecording : isUp ? undefined : startRecording}
          disabled={isUp}
          className={`relative w-28 h-28 rounded-full flex items-center justify-center transition-all shadow-lg ${
            isRec
              ? 'bg-rose-500 hover:bg-rose-600 ring-4 ring-rose-200 animate-pulse'
              : isUp
              ? 'bg-slate-300'
              : 'bg-cyan-600 hover:bg-cyan-700 ring-4 ring-cyan-100'
          }`}
          data-testid={isRec ? 'voice-recap-stop' : 'voice-recap-start'}
          aria-label={isRec ? 'Stop recording' : 'Start recording'}
        >
          {isUp ? (
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          ) : isRec ? (
            <MicOff className="w-10 h-10 text-white" />
          ) : (
            <Mic className="w-10 h-10 text-white" />
          )}
          {isRec && (
            <span
              className="absolute -bottom-2 px-2 py-0.5 rounded-full bg-rose-600 text-white text-xs font-bold tabular-nums"
              data-testid="voice-recap-timer"
            >
              ● {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}
            </span>
          )}
        </button>

        {/* Iter-267 — live mic input-level meter. Confirms audio is actually
            being captured (and helps the talent realise if they're muted). */}
        {isRec && (
          <div className="w-56 flex items-center gap-2" data-testid="mic-level-meter">
            <Volume2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
              <div
                className={`h-full transition-[width] duration-150 ${micLevel > 5 ? 'bg-emerald-500' : 'bg-slate-300'}`}
                style={{ width: `${micLevel}%` }}
              />
            </div>
            <span className="text-[10px] text-slate-500 w-10 text-right tabular-nums">{micLevel}%</span>
          </div>
        )}

        {isRec && (
          <div className="text-[11px] text-slate-500" data-testid="recording-hint">
            {isShort ? `Keep going — at least ${MIN_SECONDS}s` : micLevel < 5 ? 'Speak louder — we\'re not picking up audio' : 'Sounds great. Tap stop when done.'}
          </div>
        )}

        {/* Iter-267 — upload progress */}
        {isUp && uploadProgress > 0 && (
          <div className="w-56" data-testid="upload-progress">
            <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-cyan-500 transition-[width]" style={{ width: `${uploadProgress}%` }} />
            </div>
            <div className="text-[10px] text-slate-400 text-center mt-1 tabular-nums">{uploadProgress}%</div>
          </div>
        )}

        {error && (
          <div className="text-rose-700 text-xs bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2 max-w-sm" data-testid="voice-recap-error">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        {showPermHelp && (
          <details className="text-[11px] text-slate-500 max-w-sm" data-testid="mic-perm-help">
            <summary className="cursor-pointer flex items-center gap-1 text-cyan-700 font-medium">
              <HelpCircle className="w-3 h-3" /> How do I allow microphone access?
            </summary>
            <div className="mt-2 space-y-1.5 leading-relaxed">
              <p><strong>iPhone Safari:</strong> Settings → Safari → Microphone → Allow. Then reload this page.</p>
              <p><strong>Android Chrome:</strong> Tap the lock/info icon in the address bar → Permissions → Microphone → Allow.</p>
              <p><strong>Samsung Internet:</strong> Three-dot menu → Settings → Sites and downloads → Site permissions → Microphone.</p>
              <p><strong>Desktop Chrome:</strong> Lock icon in address bar → Site Settings → Microphone → Allow → reload.</p>
            </div>
          </details>
        )}

        <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-2">
          <Sparkles className="w-3 h-3" /> Whisper transcribes + GPT writes a draft for your review
        </div>
        {onCancel && phase === 'idle' && (
          <Button variant="ghost" size="sm" onClick={onCancel} className="text-xs text-slate-500" data-testid="voice-recap-cancel">
            I&apos;d rather type my recap
          </Button>
        )}
      </div>
    );
  }

  // ---------- REVIEW ----------
  if (phase === 'review' || phase === 'confirming') {
    return (
      <div className="space-y-4" data-testid="voice-recap-review">
        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-cyan-700 mb-1">
            <Sparkles className="w-3 h-3" /> AI Draft — review and edit before submitting
          </div>
          {draft.transcript && (
            <details className="text-[11px] text-slate-500 mt-1">
              <summary className="cursor-pointer">Show transcript</summary>
              <p className="mt-1 italic whitespace-pre-wrap">{draft.transcript}</p>
            </details>
          )}
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-700 mb-1">Overall rating</div>
          <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setDraft((d) => ({ ...d, rating: n }))}
                      className={`p-1.5 rounded-full border ${draft.rating === n ? 'bg-amber-100 border-amber-400' : 'border-slate-200 hover:border-amber-300'}`}
                      data-testid={`voice-recap-rating-${n}`}>
                <Star className={`w-5 h-5 ${draft.rating >= n ? 'text-amber-500 fill-amber-400' : 'text-slate-300'}`} />
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-700 mb-1">Highlights</div>
          <ChipList items={draft.highlights} onChange={(items) => setDraft((d) => ({ ...d, highlights: items }))} placeholder="Add a highlight" testIdPrefix="voice-recap-highlight" />
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-700 mb-1">Concerns or issues</div>
          <ChipList items={draft.concerns} onChange={(items) => setDraft((d) => ({ ...d, concerns: items }))} placeholder="Add a concern" testIdPrefix="voice-recap-concern" />
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-700 mb-1">Summary</div>
          <Textarea rows={3} value={draft.summary || ''}
                    onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))}
                    placeholder="A few sentences about the shift overall."
                    data-testid="voice-recap-summary" />
        </div>

        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2" data-testid="voice-recap-metrics">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-violet-900">
            <Sparkles className="w-3.5 h-3.5" /> Activation metrics (AI-extracted — please confirm)
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              ['leads_count',          'Leads',           'leads'],
              ['qualified_leads',      'Qualified leads', 'qualified'],
              ['samples_distributed',  'Samples',         'samples'],
              ['demos_completed',      'Demos',           'demos'],
              ['consumer_engagements', 'Engagements',     'engagements'],
            ].map(([key, label, tid]) => (
              <label key={key} className="flex items-center justify-between gap-2 bg-white border border-violet-100 rounded px-2 py-1.5">
                <span className="text-[11px] text-slate-700">{label}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  className="w-16 text-right text-sm border border-violet-200 rounded px-1.5 py-0.5"
                  value={draft[key] ?? ''}
                  placeholder="—"
                  onChange={(e) => {
                    const v = e.target.value;
                    setDraft((d) => ({ ...d, [key]: v === '' ? null : Math.max(0, parseInt(v, 10) || 0) }));
                  }}
                  data-testid={`voice-recap-metric-${tid}`}
                />
              </label>
            ))}
          </div>
          <p className="text-[10px] text-violet-700">
            These numbers go straight into the client&apos;s Campaign Intelligence dashboard. Leave blank if you didn&apos;t mention them.
          </p>
        </div>

        {error && (
          <div className="text-rose-700 text-xs bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <Button variant="ghost" size="sm" onClick={() => { setPhase('idle'); setDraft(null); setSeconds(0); durationRef.current = 0; startedAtRef.current = 0; }}
                  data-testid="voice-recap-redo">
            Re-record
          </Button>
          <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5"
                  disabled={phase === 'confirming'}
                  onClick={confirm}
                  data-testid="voice-recap-confirm">
            {phase === 'confirming' ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Submit recap
          </Button>
        </div>
      </div>
    );
  }

  // ---------- DONE ----------
  return (
    <div className="flex flex-col items-center gap-2 py-6" data-testid="voice-recap-done">
      <CheckCircle2 className="w-10 h-10 text-emerald-500" />
      <p className="text-sm font-medium text-slate-900">Recap submitted</p>
      <p className="text-xs text-slate-500">Thanks — this helps us match you better next time.</p>
    </div>
  );
}


function ChipList({ items, onChange, placeholder, testIdPrefix }) {
  const [draft, setDraftLocal] = useState('');
  const add = () => {
    const v = (draft || '').trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setDraftLocal('');
  };
  return (
    <div className="space-y-1.5">
      {(items || []).map((it, i) => (
        <div key={`${i}-${it}`} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1">
          <input
            className="flex-1 bg-transparent text-xs outline-none"
            value={it}
            onChange={(e) => {
              const next = items.slice();
              next[i] = e.target.value;
              onChange(next);
            }}
            data-testid={`${testIdPrefix}-${i}`}
          />
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}
                  className="text-slate-400 hover:text-rose-600"
                  data-testid={`${testIdPrefix}-${i}-remove`}>
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-1.5">
        <input
          className="flex-1 text-xs border border-slate-200 rounded px-2 py-1"
          value={draft}
          onChange={(e) => setDraftLocal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={`${testIdPrefix}-add-input`}
        />
        <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={add}
                data-testid={`${testIdPrefix}-add-btn`}>
          Add
        </Button>
      </div>
    </div>
  );
}
